// src/pages/Admin/ReglementInscription/CreateReglementInscriptionModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardList,
  FaCoins,
  FaCreditCard,
  FaEuroSign,
  FaFileInvoiceDollar,
  FaIdCard,
  FaInfoCircle,
  FaMoneyBillWave,
  FaReceipt,
  FaSave,
  FaSearch,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
  FaUser,
  FaUserCheck,
} from "react-icons/fa";

import { createReglementInscription } from "../../../services/reglementinscriptionServices";
import { getAllAdherentsList } from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import {
  getAllDossiersInscription,
  updateDossierInscription,
} from "../../../services/dossierServices";

const fmtEUR = (value) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const inputClass =
  "min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const STATUS_OPTIONS = [
  {
    value: "Réglé",
    label: "Réglé",
    description: "Dossier validé côté règlement.",
    icon: FaCheckCircle,
    activeClass:
      "bg-emerald-100 text-emerald-900 ring-emerald-300 dark:bg-emerald-900/35 dark:text-emerald-100 dark:ring-emerald-700",
    softClass:
      "bg-emerald-50/70 text-emerald-800 ring-emerald-100 hover:bg-emerald-100/80 dark:bg-emerald-950/20 dark:text-emerald-200 dark:ring-emerald-900/60 dark:hover:bg-emerald-900/25",
  },
  {
    value: "Partiellement réglé",
    label: "Partiel",
    description: "Dossier considéré comme en cours de validation.",
    icon: FaCheckCircle,
    activeClass:
      "bg-lime-100 text-lime-900 ring-lime-300 dark:bg-lime-900/35 dark:text-lime-100 dark:ring-lime-700",
    softClass:
      "bg-lime-50/80 text-lime-800 ring-lime-100 hover:bg-lime-100/80 dark:bg-lime-950/20 dark:text-lime-200 dark:ring-lime-900/60 dark:hover:bg-lime-900/25",
  },
  {
    value: "En cours",
    label: "En cours",
    description: "Paiement à suivre.",
    icon: FaSpinner,
    activeClass:
      "bg-amber-100 text-amber-900 ring-amber-300 dark:bg-amber-900/35 dark:text-amber-100 dark:ring-amber-700",
    softClass:
      "bg-amber-50/80 text-amber-800 ring-amber-100 hover:bg-amber-100/80 dark:bg-amber-950/20 dark:text-amber-200 dark:ring-amber-900/60 dark:hover:bg-amber-900/25",
  },
  {
    value: "Non réglé",
    label: "Non réglé",
    description: "Aucun règlement validé.",
    icon: FaTimes,
    activeClass:
      "bg-red-100 text-red-900 ring-red-300 dark:bg-red-900/35 dark:text-red-100 dark:ring-red-700",
    softClass:
      "bg-red-50/75 text-red-800 ring-red-100 hover:bg-red-100/80 dark:bg-red-950/20 dark:text-red-200 dark:ring-red-900/60 dark:hover:bg-red-900/25",
  },
  {
    value: "Problème",
    label: "Problème",
    description: "À traiter manuellement.",
    icon: FaInfoCircle,
    activeClass:
      "bg-violet-100 text-violet-900 ring-violet-300 dark:bg-violet-900/35 dark:text-violet-100 dark:ring-violet-700",
    softClass:
      "bg-violet-50/75 text-violet-800 ring-violet-100 hover:bg-violet-100/80 dark:bg-violet-950/20 dark:text-violet-200 dark:ring-violet-900/60 dark:hover:bg-violet-900/25",
  },
  {
    value: "Annulé",
    label: "Annulé",
    description: "Règlement annulé.",
    icon: FaTimes,
    activeClass:
      "bg-slate-200 text-slate-900 ring-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600",
    softClass:
      "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-800",
  },
];

const PAYMENT_TYPES = [
  "En ligne",
  "Chèque",
  "Espèces",
  "Virement",
  "CB",
  "Chèques Vacances",
  "Coupons Sports",
  "Autre",
];

function neighborsFromSeason(season) {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(season || "");

  if (!match) return [season].filter(Boolean);

  const yearStart = parseInt(match[1], 10);
  const yearEnd = parseInt(match[2], 10);

  return [
    `${yearStart - 1}-${yearEnd - 1}`,
    `${yearStart}-${yearEnd}`,
    `${yearStart + 1}-${yearEnd + 1}`,
  ];
}

const normalizeAmount = (value) =>
  Number(String(value || "").replace(",", ".")) || 0;

const toAbsolute = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (!cleanUrl.startsWith("/uploads")) return cleanUrl;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${cleanUrl}`;
};

const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

const getAdherentPhoto = (adherent) => {
  if (!adherent) return "/img/user_avatar.jpg";

  if (adherent?.autorisation_photo === false) {
    return "/img/interdiction.jpg";
  }

  return (
    toAbsolute(
      adherent?.photo_url ||
        adherent?.photoURL ||
        adherent?.photo ||
        adherent?.avatar,
    ) || getDefaultPhoto(adherent?.genre)
  );
};

const getAdherentName = (adherent) => {
  if (!adherent) return "Aucun adhérent sélectionné";

  const nom = String(adherent?.nom || "")
    .trim()
    .toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
};

function SectionCard({
  icon: Icon,
  title,
  description,
  children,
  className = "",
}) {
  return (
    <section
      className={`relative rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80 ${className}`}
    >
      <div className="rounded-t-2xl border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function PreviewLine({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
        {value || "—"}
      </p>
    </div>
  );
}

function AmountField({ label, name, value, onChange, placeholder, disabled }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>

      <div className="relative">
        <FaEuroSign className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          type="number"
          step="0.01"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`${inputClass} pl-9`}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function AdherentPicker({ adherents, value, onSelect, disabled }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const inputRef = useRef(null);

  const selected = useMemo(
    () =>
      adherents.find((adherent) => String(adherent.id) === String(value)) ||
      null,
    [adherents, value],
  );

  const filtered = useMemo(() => {
    const cleanQuery = query.trim().toLowerCase();

    const sorted = [...adherents].sort((a, b) =>
      `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
        `${b.nom || ""} ${b.prenom || ""}`,
        "fr",
        { sensitivity: "base" },
      ),
    );

    if (!cleanQuery) return sorted.slice(0, 50);

    return sorted
      .filter((adherent) => {
        const text = `${adherent.nom || ""} ${adherent.prenom || ""} ${
          adherent.email || ""
        } ${adherent.tel || ""}`.toLowerCase();

        return text.includes(cleanQuery);
      })
      .slice(0, 50);
  }, [adherents, query]);

  useEffect(() => {
    if (!open) setHighlight(0);
  }, [open]);

  const choose = (adherent) => {
    onSelect(adherent);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (event) => {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      setOpen(true);
      return;
    }

    if (!open) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, filtered.length - 1));
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const item = filtered[highlight];

      if (item) choose(item);
    }

    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <label className={labelClass}>
        Adhérent <span className="text-pink-600">*</span>
      </label>

      {selected && (
        <div className="mb-3 flex items-center gap-3 rounded-2xl bg-violet-50 p-3 ring-1 ring-violet-200 dark:bg-violet-900/20 dark:ring-violet-800/60">
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-2xl bg-white ring-1 ring-violet-100 dark:bg-slate-950 dark:ring-violet-800">
            <img
              src={getAdherentPhoto(selected)}
              alt={getAdherentName(selected)}
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.src = "/img/user_avatar.jpg";
              }}
            />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-violet-900 dark:text-violet-100">
              {getAdherentName(selected)}
            </p>
            <p className="text-xs text-violet-700/75 dark:text-violet-200/75">
              #{selected.id}
              {selected.email ? ` · ${selected.email}` : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={() => onSelect(null)}
            disabled={disabled}
            className="rounded-xl bg-white px-3 py-2 text-xs font-bold text-violet-700 ring-1 ring-violet-200 transition hover:bg-violet-100 disabled:opacity-60 dark:bg-slate-950 dark:text-violet-200 dark:ring-violet-800 dark:hover:bg-slate-900"
          >
            Changer
          </button>
        </div>
      )}

      <div className="relative">
        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          ref={inputRef}
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher nom, prénom, email…"
          className={`${inputClass} pl-9`}
          disabled={disabled}
        />
      </div>

      {open && filtered.length > 0 && !disabled && (
        <div className="absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
          {filtered.map((adherent, index) => (
            <button
              type="button"
              key={adherent.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(adherent)}
              className={`flex w-full items-center gap-3 px-3 py-3 text-left text-sm transition ${
                index === highlight
                  ? "bg-violet-50 dark:bg-violet-900/20"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
              }`}
            >
              <div className="h-10 w-10 shrink-0 overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                <img
                  src={getAdherentPhoto(adherent)}
                  alt={getAdherentName(adherent)}
                  className="h-full w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.src = "/img/user_avatar.jpg";
                  }}
                />
              </div>

              <div className="min-w-0">
                <div className="truncate font-bold text-slate-900 dark:text-white">
                  {getAdherentName(adherent)}{" "}
                  <span className="text-xs font-semibold text-slate-400">
                    #{adherent.id}
                  </span>
                </div>

                {adherent.email && (
                  <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {adherent.email}
                  </div>
                )}
              </div>
            </button>
          ))}

          {filtered.length >= 50 && (
            <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
              Affichage limité à 50 résultats — affinez la recherche.
            </div>
          )}
        </div>
      )}

      <input type="hidden" name="adherent_id" value={selected?.id || ""} />
    </div>
  );
}

function ReglementPreviewCard({ adherent, form, liveTotal, activeSeason }) {
  const status = STATUS_OPTIONS.find(
    (item) => item.value === form.statut_reglement,
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <FaReceipt className="text-xl" />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-white/70">
              Règlement
            </p>
            <p className="truncate text-lg font-bold">Inscription</p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex flex-col items-center text-center">
          <div className="h-32 w-32 overflow-hidden rounded-[2rem] bg-slate-100 shadow-inner ring-4 ring-white dark:bg-slate-800 dark:ring-slate-900">
            {adherent ? (
              <img
                src={getAdherentPhoto(adherent)}
                alt={getAdherentName(adherent)}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = "/img/user_avatar.jpg";
                }}
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-slate-400">
                <FaUser className="text-3xl" />
                <span className="px-3 text-xs font-semibold">
                  Sélectionnez un adhérent
                </span>
              </div>
            )}
          </div>

          <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
            {adherent ? getAdherentName(adherent) : "Aucun adhérent"}
          </h3>

          {status && (
            <span
              className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${status.tone}`}
            >
              {status.value}
            </span>
          )}
        </div>

        <div className="mt-5 rounded-[1.5rem] bg-slate-950 p-4 text-white shadow-lg dark:bg-slate-900 dark:ring-1 dark:ring-slate-800">
          <p className="text-xs font-bold uppercase tracking-wide text-white/50">
            Total provisoire
          </p>
          <p className="mt-1 text-3xl font-bold">{fmtEUR(liveTotal)}</p>
          <p className="mt-1 text-xs text-white/60">
            Les réductions sont prises en compte selon les montants saisis.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2">
          <PreviewLine
            label="Saison"
            value={form.saison || activeSeason || "—"}
          />
          <PreviewLine label="Type" value={form.type_reglement || "—"} />
          <PreviewLine label="Nombre" value={form.nb_reglement || "1"} />
        </div>
      </div>
    </section>
  );
}

function StatusSelector({ value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {STATUS_OPTIONS.map((status) => {
        const active = value === status.value;
        const Icon = status.icon;

        return (
          <button
            key={status.value}
            type="button"
            onClick={() => onChange(status.value)}
            disabled={disabled}
            className={`group rounded-2xl p-3 text-left ring-1 transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${
              active ? status.activeClass : status.softClass
            }`}
          >
            <span className="flex items-center gap-2 text-sm font-bold">
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${
                  active
                    ? "bg-white/70 text-current dark:bg-white/10"
                    : "bg-white/80 text-current dark:bg-white/10"
                }`}
              >
                <Icon
                  className={`text-xs ${
                    status.value === "En cours" && active ? "animate-spin" : ""
                  }`}
                />
              </span>

              {status.label}

              {active && (
                <span className="ml-auto rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide dark:bg-white/10">
                  choisi
                </span>
              )}
            </span>

            <span className="mt-1.5 block text-xs leading-relaxed opacity-75">
              {status.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const CreateReglementInscriptionModal = ({
  isModalOpen,
  setIsModalOpen,
  fetchReglements,
  onClose,
}) => {
  const dialogRef = useRef(null);

  const [adherents, setAdherents] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    adherent_id: "",
    saison: "",
    montant_lic_assurance: "",
    montant_cotisation: "",
    reduction_foyer: "",
    pass_sport: "",
    reduction_saison: "",
    type_reglement: "",
    nb_reglement: "1",
    observations: "",
    statut_reglement: "",
  });

  const selectedAdherent = useMemo(
    () =>
      adherents.find(
        (adherent) => String(adherent.id) === String(form.adherent_id),
      ) || null,
    [adherents, form.adherent_id],
  );

  const liveTotal = useMemo(() => {
    return (
      normalizeAmount(form.montant_lic_assurance) +
      normalizeAmount(form.montant_cotisation) +
      normalizeAmount(form.reduction_foyer) +
      normalizeAmount(form.pass_sport) +
      normalizeAmount(form.reduction_saison)
    );
  }, [
    form.montant_lic_assurance,
    form.montant_cotisation,
    form.reduction_foyer,
    form.pass_sport,
    form.reduction_saison,
  ]);

  const handleClose = useCallback(() => {
    if (submitting) return;

    if (onClose) {
      onClose();
    } else {
      setIsModalOpen?.(false);
    }
  }, [onClose, setIsModalOpen, submitting]);

  const loadContext = useCallback(async () => {
    setLoading(true);

    try {
      const [ads, settings] = await Promise.all([
        getAllAdherentsList(),
        getSettings(),
      ]);

      setAdherents(Array.isArray(ads) ? ads : []);

      const active = settings?.[0]?.saison_active || "";

      setActiveSeason(active);
      setSeasons(neighborsFromSeason(active));

      setForm((previous) => ({
        ...previous,
        saison: active || previous.saison || "",
      }));
    } catch (error) {
      console.error(error);
      toast.error("Impossible de charger les adhérents / paramètres.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;

    setForm({
      adherent_id: "",
      saison: "",
      montant_lic_assurance: "",
      montant_cotisation: "",
      reduction_foyer: "",
      pass_sport: "",
      reduction_saison: "",
      type_reglement: "",
      nb_reglement: "1",
      observations: "",
      statut_reglement: "",
    });

    loadContext();
  }, [isModalOpen, loadContext]);

  useEffect(() => {
    if (!isModalOpen) return;

    const root = window.document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const timer = setTimeout(() => {
      dialogRef.current?.querySelector("input[type='text']")?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      root.style.overflow = previousOverflow;
    };
  }, [isModalOpen, handleClose]);

  const handleBasicChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const setStatus = (status) => {
    setForm((previous) => ({
      ...previous,
      statut_reglement: status,
    }));
  };

  const canSubmit = useMemo(() => {
    return (
      !loading &&
      !submitting &&
      String(form.adherent_id || "").trim() !== "" &&
      String(form.statut_reglement || "").trim() !== ""
    );
  }, [form.adherent_id, form.statut_reglement, loading, submitting]);

  const syncDossierValidateReglement = useCallback(
    async (adherentId, saison, statut) => {
      try {
        const validated =
          statut === "Réglé" || statut === "Partiellement réglé";

        const dossiers = await getAllDossiersInscription();
        const list = Array.isArray(dossiers) ? dossiers : [];

        const target = list.find((dossier) => {
          const dossierAdherentId =
            dossier?.inscription_adherent_id || dossier?.Adherent?.id;
          const dossierSeason =
            dossier?.saison || dossier?.Adherent?.saison_active;

          return (
            String(dossierAdherentId) === String(adherentId) &&
            String(dossierSeason) === String(saison)
          );
        });

        if (!target) {
          console.warn(
            "[CreateReglement] Aucun dossier trouvé pour adherent=",
            adherentId,
            " saison=",
            saison,
          );
          return;
        }

        await updateDossierInscription(target.id, {
          validate_reglement: validated,
        });
      } catch (error) {
        console.error(
          "[CreateReglement] Erreur synchronisation dossier.validate_reglement:",
          error,
        );

        toast.error(
          "Règlement créé, mais la synchronisation du dossier a échoué.",
        );
      }
    },
    [],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      toast.warn("Sélectionnez un adhérent et un statut de règlement.");
      return;
    }

    setSubmitting(true);

    try {
      const numOrZero = (value) =>
        value === "" || value === null || value === undefined
          ? 0
          : Number(String(value).replace(",", "."));

      const payload = {
        adherent_id: form.adherent_id,
        saison: form.saison || activeSeason || null,
        montant_lic_assurance: numOrZero(form.montant_lic_assurance),
        montant_cotisation: numOrZero(form.montant_cotisation),
        reduction_foyer: numOrZero(form.reduction_foyer),
        pass_sport: numOrZero(form.pass_sport),
        reduction_saison: numOrZero(form.reduction_saison),
        type_reglement: form.type_reglement || "",
        nb_reglement: (() => {
          const count = Number(form.nb_reglement);
          return Number.isFinite(count) && count > 0 ? count : 1;
        })(),
        observations: form.observations || "",
        statut_reglement: form.statut_reglement,
      };

      await createReglementInscription(payload);

      await syncDossierValidateReglement(
        payload.adherent_id,
        payload.saison || activeSeason,
        payload.statut_reglement,
      );

      toast.success("Règlement créé avec succès.");

      fetchReglements?.();
      handleClose();
    } catch (error) {
      console.error("Erreur création règlement", error);
      toast.error("Erreur lors de la création du règlement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isModalOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Créer un règlement d’inscription"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[94dvh] w-full max-w-7xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-4 py-5 text-white sm:px-6">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                Règlements inscriptions
              </p>

              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">
                Créer un règlement
              </h3>

              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/85">
                Créez un règlement d’inscription pour un adhérent.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="create-reglement-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="space-y-5">
                <ReglementPreviewCard
                  adherent={selectedAdherent}
                  form={form}
                  liveTotal={liveTotal}
                  activeSeason={activeSeason}
                />

                <SectionCard
                  icon={FaInfoCircle}
                  title="Résumé"
                  description="Contrôle rapide avant création."
                >
                  <div className="space-y-3">
                    <PreviewLine
                      label="Adhérent"
                      value={
                        selectedAdherent
                          ? getAdherentName(selectedAdherent)
                          : "—"
                      }
                    />
                    <PreviewLine
                      label="Saison"
                      value={form.saison || activeSeason || "—"}
                    />
                    <PreviewLine
                      label="Statut"
                      value={form.statut_reglement || "—"}
                    />
                    <PreviewLine label="Total" value={fmtEUR(liveTotal)} />
                  </div>

                  <div className="mt-4 rounded-2xl bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:ring-blue-900">
                    Les statuts “Réglé” et “Partiellement réglé” valideront le
                    règlement dans le dossier d’inscription.
                  </div>
                </SectionCard>
              </aside>

              <main className="space-y-5">
                <SectionCard
                  icon={FaUserCheck}
                  title="Identité et saison"
                  description="L’adhérent est obligatoire. La saison active est proposée automatiquement."
                >
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <AdherentPicker
                      adherents={adherents}
                      value={form.adherent_id}
                      disabled={loading || submitting}
                      onSelect={(adherent) =>
                        setForm((previous) => ({
                          ...previous,
                          adherent_id: adherent ? adherent.id : "",
                        }))
                      }
                    />

                    <div>
                      <label className={labelClass}>Saison</label>

                      <div className="relative">
                        <FaCalendarAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <select
                          name="saison"
                          value={form.saison}
                          onChange={handleBasicChange}
                          disabled={loading || submitting}
                          className={`${inputClass} pl-9`}
                        >
                          <option value="">
                            {loading
                              ? "Chargement…"
                              : activeSeason
                                ? `Saison active par défaut : ${activeSeason}`
                                : "— Laisser vide si inconnu —"}
                          </option>

                          {seasons.map((season) => (
                            <option key={season} value={season}>
                              {season}
                            </option>
                          ))}
                        </select>
                      </div>

                      <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                        Si vous laissez vide, la saison{" "}
                        {activeSeason ? (
                          <span className="font-bold">{activeSeason}</span>
                        ) : (
                          "active"
                        )}{" "}
                        sera utilisée.
                      </p>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaCoins}
                  title="Montants"
                  description="Saisissez les montants. Les réductions doivent être négatives si elles diminuent le total."
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <AmountField
                      label="Licence & Assurance"
                      name="montant_lic_assurance"
                      value={form.montant_lic_assurance}
                      onChange={handleBasicChange}
                      placeholder="0,00"
                      disabled={submitting}
                    />

                    <AmountField
                      label="Cotisation"
                      name="montant_cotisation"
                      value={form.montant_cotisation}
                      onChange={handleBasicChange}
                      placeholder="0,00"
                      disabled={submitting}
                    />

                    <AmountField
                      label="Réduction foyer"
                      name="reduction_foyer"
                      value={form.reduction_foyer}
                      onChange={handleBasicChange}
                      placeholder="ex. -20,00"
                      disabled={submitting}
                    />

                    <AmountField
                      label="Pass’Sport"
                      name="pass_sport"
                      value={form.pass_sport}
                      onChange={handleBasicChange}
                      placeholder="ex. -70,00"
                      disabled={submitting}
                    />

                    <AmountField
                      label="Réduction saison"
                      name="reduction_saison"
                      value={form.reduction_saison}
                      onChange={handleBasicChange}
                      placeholder="ex. -10,00"
                      disabled={submitting}
                    />

                    <div className="rounded-2xl bg-slate-950 p-4 text-white shadow-lg dark:bg-slate-900 dark:ring-1 dark:ring-slate-800">
                      <p className="text-xs font-bold uppercase tracking-wide text-white/50">
                        Total provisoire
                      </p>
                      <p className="mt-1 text-2xl font-bold">
                        {fmtEUR(liveTotal)}
                      </p>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaCreditCard}
                  title="Mode et statut du règlement"
                  description="Le statut est obligatoire pour finaliser la création."
                >
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className={labelClass}>Type de règlement</label>

                        <div className="relative">
                          <FaMoneyBillWave className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                          <select
                            name="type_reglement"
                            value={form.type_reglement}
                            onChange={handleBasicChange}
                            className={`${inputClass} pl-9`}
                            disabled={submitting}
                          >
                            <option value="">— Sélectionner —</option>
                            {PAYMENT_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className={labelClass}>
                          Nombre de règlements
                        </label>

                        <div className="relative">
                          <FaFileInvoiceDollar className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                          <input
                            type="number"
                            name="nb_reglement"
                            min={1}
                            inputMode="numeric"
                            value={form.nb_reglement}
                            onChange={handleBasicChange}
                            placeholder="ex. 1, 2, 3…"
                            className={`${inputClass} pl-9`}
                            disabled={submitting}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>
                        Statut <span className="text-pink-600">*</span>
                      </label>

                      <select
                        name="statut_reglement"
                        value={form.statut_reglement}
                        onChange={handleBasicChange}
                        className={`${inputClass} mb-3`}
                        required
                        disabled={submitting}
                      >
                        <option value="">— Sélectionner —</option>
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.value}
                          </option>
                        ))}
                      </select>

                      <StatusSelector
                        value={form.statut_reglement}
                        onChange={setStatus}
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>
                        Observations / N° Pass’Sport
                      </label>

                      <div className="relative">
                        <FaClipboardList className="pointer-events-none absolute left-3 top-4 text-slate-400" />

                        <textarea
                          name="observations"
                          value={form.observations}
                          onChange={handleBasicChange}
                          rows={4}
                          placeholder="Notes libres, numéro Pass’Sport, précision sur les échéances…"
                          className={`${inputClass} resize-none pl-9 leading-relaxed`}
                          disabled={submitting}
                        />
                      </div>
                    </div>
                  </div>
                </SectionCard>

                {/* <SectionCard
                  icon={FaShieldAlt}
                  title="Synchronisation dossier"
                  description="Le dossier d’inscription sera mis à jour après création du règlement."
                >
                  <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                    <p className="text-sm font-bold text-slate-800 dark:text-white">
                      Validation automatique :{" "}
                      {form.statut_reglement === "Réglé" ||
                      form.statut_reglement === "Partiellement réglé"
                        ? "oui"
                        : "non"}
                    </p>

                    <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                      Si le dossier correspondant à l’adhérent et à la saison
                      est trouvé, son champ <code>validate_reglement</code> sera
                      mis à jour automatiquement.
                    </p>
                  </div>
                </SectionCard> */}
              </main>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <FaSave />
                    Enregistrer le règlement
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .create-reglement-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .create-reglement-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.45);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        .create-reglement-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dark .create-reglement-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border: 3px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>,
    window.document.body,
  );
};

export default CreateReglementInscriptionModal;
